/**
 * Depth-limited blur utility.
 * Copyright Higharc Inc. 2020
 */
import * as THREE from "three";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import { DepthLimitedBlurShader2, BlurShaderUtils2 } from "./DepthLimitedBlurShader2";
import type { DepthNormalPass } from "./DepthNormalPass";
import { screenSpaceViewNormalPrepareShader, screenSpaceViewPositionPrepareShader } from "./screenSpaceHelpersGLSL";

export interface DepthLimitedBlurParams {
  // These parameters are mapped to shader defines, changing them will cause a recompile.
  kernelSamples: number;
  stdDev: number;
  depthCutoff: number;
  normalAngleCutoff: number;
  normalCutoffEnabled: boolean;
  depthCutoffBasedOnNormal: boolean;
  useNormalTexture: boolean;
}

export class DepthLimitedBlur {
  readonly params: DepthLimitedBlurParams;

  private vBlurMaterial: THREE.ShaderMaterial;
  private hBlurMaterial: THREE.ShaderMaterial;

  private prevKernelSamples: number;
  private prevStdDev: number;

  private fsQuad: FullScreenQuad;

  constructor() {
    const getShaderDefine = (defineName: string): boolean => {
      return this.vBlurMaterial.defines[defineName] === 1;
    };
    const setShaderDefine = (defineName: string, value: boolean) => {
      const defineValue = value ? 1 : 0;
      if (this.vBlurMaterial.defines[defineName] === defineValue) {
        return;
      }
      this.vBlurMaterial.defines[defineName] = defineValue;
      this.hBlurMaterial.defines[defineName] = defineValue;
      this.refreshShaders();
    };

    let useNormalTexture = true;
    this.params = {
      kernelSamples: 6, // number of blur samples taken is double this value
      stdDev: 4,
      depthCutoff: 1.0,
      normalAngleCutoff: Math.PI * 0.3,
      get useNormalTexture(): boolean {
        return useNormalTexture;
      },
      set useNormalTexture(value: boolean) {
        if (useNormalTexture === value) {
          return;
        }
        useNormalTexture = value;
        this.refreshShaders();
      },
      get normalCutoffEnabled(): boolean {
        return getShaderDefine("NORMAL_CUTOFF");
      },
      set normalCutoffEnabled(value: boolean) {
        setShaderDefine("NORMAL_CUTOFF", value);
      },
      get depthCutoffBasedOnNormal(): boolean {
        return getShaderDefine("DEPTH_CUTOFF_BASED_ON_NORMAL");
      },
      set depthCutoffBasedOnNormal(value: boolean) {
        setShaderDefine("DEPTH_CUTOFF_BASED_ON_NORMAL", value);
      },
    };

    this.hBlurMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(DepthLimitedBlurShader2.uniforms),
      defines: Object.assign({}, DepthLimitedBlurShader2.defines),
      vertexShader: DepthLimitedBlurShader2.vertexShader,
      fragmentShader: DepthLimitedBlurShader2.fragmentShader,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "DepthLimitedBlur hBlurMaterial",
    });

    this.vBlurMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(DepthLimitedBlurShader2.uniforms),
      defines: Object.assign({}, DepthLimitedBlurShader2.defines),
      vertexShader: DepthLimitedBlurShader2.vertexShader,
      fragmentShader: DepthLimitedBlurShader2.fragmentShader,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "DepthLimitedBlur vBlurMaterial",
    });

    this.refreshShaders();

    this.prevKernelSamples = -1;
    this.prevStdDev = -1;

    this.fsQuad = new FullScreenQuad();
  }

  blur(
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    sourceTexture: THREE.Texture,
    intermediateRenderTarget: THREE.WebGLRenderTarget,
    finalRenderTarget: THREE.WebGLRenderTarget,
    depthNormalPass: DepthNormalPass,
    blurColorMask: [boolean, boolean, boolean, boolean],
  ) {
    const vBlurMaterial = this.vBlurMaterial;
    const hBlurMaterial = this.hBlurMaterial;

    this.params.kernelSamples = Math.floor(this.params.kernelSamples);
    if (this.prevStdDev !== this.params.stdDev || this.prevKernelSamples !== this.params.kernelSamples) {
      BlurShaderUtils2.configureKernel(
        vBlurMaterial,
        this.params.kernelSamples,
        this.params.stdDev,
        new THREE.Vector2(0, 1),
      );
      BlurShaderUtils2.configureKernel(
        hBlurMaterial,
        this.params.kernelSamples,
        this.params.stdDev,
        new THREE.Vector2(1, 0),
      );
      this.prevStdDev = this.params.stdDev;
      this.prevKernelSamples = this.params.kernelSamples;
    }

    vBlurMaterial.uniforms["tDiffuse"].value = sourceTexture;
    hBlurMaterial.uniforms["tDiffuse"].value = intermediateRenderTarget.texture;

    vBlurMaterial.uniforms["invSize"].value.set(
      1.0 / intermediateRenderTarget.width,
      1.0 / intermediateRenderTarget.height,
    );
    hBlurMaterial.uniforms["invSize"].value.set(1.0 / finalRenderTarget.width, 1.0 / finalRenderTarget.height);

    screenSpaceViewPositionPrepareShader(vBlurMaterial, camera, depthNormalPass);
    screenSpaceViewNormalPrepareShader(vBlurMaterial, depthNormalPass);
    screenSpaceViewPositionPrepareShader(hBlurMaterial, camera, depthNormalPass);
    screenSpaceViewNormalPrepareShader(hBlurMaterial, depthNormalPass);

    const depthCutoff = this.params.depthCutoff;
    vBlurMaterial.uniforms["depthCutoff"].value = depthCutoff;
    hBlurMaterial.uniforms["depthCutoff"].value = depthCutoff;

    const normalCutoff = BlurShaderUtils2.normalDotProductCutoffFromAngle(this.params.normalAngleCutoff);
    vBlurMaterial.uniforms["normalDotProductCutoff"].value = normalCutoff;
    hBlurMaterial.uniforms["normalDotProductCutoff"].value = normalCutoff;

    vBlurMaterial.uniforms["colorMask"].value = blurColorMask;
    hBlurMaterial.uniforms["colorMask"].value = blurColorMask;

    this.renderPass(renderer, vBlurMaterial, intermediateRenderTarget);
    this.renderPass(renderer, hBlurMaterial, finalRenderTarget);
  }

  private renderPass(renderer, passMaterial, renderTarget) {
    renderer.setRenderTarget(renderTarget);

    renderer.setClearColor(0xffffff);
    renderer.setClearAlpha(1.0);
    renderer.clear();

    this.fsQuad.material = passMaterial;
    this.fsQuad.render(renderer);
  }

  private refreshShaders() {
    this.refreshShader(this.vBlurMaterial);
    this.refreshShader(this.hBlurMaterial);
  }

  private refreshShader(material: THREE.ShaderMaterial) {
    const needsNormals = material.defines["NORMAL_CUTOFF"] === 1 || material.defines["DEPTH_CUTOFF_BASED_ON_NORMAL"];
    material.defines["NORMAL_TEXTURE"] = needsNormals && this.params.useNormalTexture ? 1 : 0;
    material.needsUpdate = true;
  }

  dispose() {
    this.vBlurMaterial.dispose();
    this.hBlurMaterial.dispose();
  }
}
